import { createApiClient, API_BASE_URL, type ApiClientOptions } from "./client";
import type { Asset } from "@/lib/types/assets";

export function createAssetsApi(options: ApiClientOptions) {
  const api = createApiClient(options);

  return {
    list: (params?: { type?: string; q?: string }) => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set("type", params.type);
      if (params?.q) qs.set("q", params.q);
      const query = qs.toString() ? `?${qs}` : "";
      return api.get<Asset[]>(`/api/assets/list${query}`);
    },

    get: (assetId: string) => api.get<Asset>(`/api/assets/${assetId}`),

    rename: (assetId: string, name: string) =>
      api.patch<Asset>(`/api/assets/${assetId}`, { name }),

    delete: (assetId: string) => api.delete<void>(`/api/assets/${assetId}`),

    fetchContent: async (assetId: string): Promise<Blob> => {
      const token = await options.getToken();
      if (!token) throw new Error("Not authenticated");

      const resp = await fetch(`${API_BASE_URL}/api/assets/${assetId}/content`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(
          typeof (err as Record<string, unknown>)?.detail === "string"
            ? String((err as Record<string, unknown>).detail)
            : `Fetch content failed: ${resp.status}`
        );
      }
      return resp.blob();
    },

    upload: async (file: File): Promise<Asset> => {
      const token = await options.getToken();
      if (!token) throw new Error("Not authenticated");

      const form = new FormData();
      form.append("file", file);

      const resp = await fetch(`${API_BASE_URL}/api/assets/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(
          typeof (err as Record<string, unknown>)?.detail === "string"
            ? String((err as Record<string, unknown>).detail)
            : `Upload failed: ${resp.status}`
        );
      }
      return resp.json() as Promise<Asset>;
    },
  };
}
